import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useEmbedStore } from '@/stores/embedStore'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('qtz-theme') as Theme
      if (saved) return saved
    }
    // Default to dark unless the user has explicitly chosen a theme before.
    return 'dark'
  })

  // Embedded: the partner parent may dictate the theme.
  const embedTheme = useEmbedStore((s) => s.theme)
  useEffect(() => {
    if (embedTheme) setTheme(embedTheme)
  }, [embedTheme])

  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
    localStorage.setItem('qtz-theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
